import type { LabelListContent } from "../../../api/content/authoring/processContentDecoders";
import type { ConflictFinding } from "../../../api/content/reviewDecoders";

/**
 * A label list in review (curation-ui.md CU-4), pure. Labels are compared by key, so a
 * renamed display name is a change, not a removal. A label that links a term the change
 * set removes or renames needs a decision: point it at the renamed term, or drop the link.
 */

type Label = LabelListContent["labels"][number];
export type LabelField = Exclude<keyof Label, "key">;

const FIELDS: readonly LabelField[] = ["name", "description", "guidelines", "examples", "ontology_terms"];

export interface ChangedLabel {
  readonly before: Label;
  readonly after: Label;
  readonly fields: readonly LabelField[];
}

export interface LabelListDiff {
  readonly added: readonly Label[];
  readonly removed: readonly Label[];
  readonly changed: readonly ChangedLabel[];
  readonly unchangedCount: number;
}

function same(left: Label[LabelField], right: Label[LabelField]): boolean {
  return Array.isArray(left) && Array.isArray(right) ? left.length === right.length && left.every((value, index) => value === right[index]) : left === right;
}

/** The labels added, removed and changed between two versions; `null` for a side that does not exist. */
export function labelListDiff(before: LabelListContent | null, after: LabelListContent | null): LabelListDiff {
  const old = new Map((before?.labels ?? []).map((label) => [label.key, label]));
  const added: Label[] = [];
  const changed: ChangedLabel[] = [];
  let unchangedCount = 0;
  for (const label of after?.labels ?? []) {
    const previous = old.get(label.key);
    old.delete(label.key);
    if (previous === undefined) {
      added.push(label);
      continue;
    }
    const fields = FIELDS.filter((field) => !same(previous[field], label[field]));
    if (fields.length === 0) {
      unchangedCount += 1;
    } else {
      changed.push({ before: previous, after: label, fields });
    }
  }
  return { added, removed: [...old.values()], changed, unchangedCount };
}

export interface BrokenLink {
  readonly labelKey: string;
  readonly labelName: string;
  /** The linked term the change set removes or renames. */
  readonly term: string;
  /** Where the conflict check says it went, if it was renamed. */
  readonly renamedTo: string | null;
}

/** Links of the head's labels to terms the change set removes or renames, from the conflict check. */
export function brokenLinks(after: LabelListContent | null, itemId: string, findings: readonly ConflictFinding[]): readonly BrokenLink[] {
  const gone = new Map<string, string | null>();
  for (const finding of findings) {
    if (finding.item?.itemId === itemId && finding.term !== null && (finding.termChangeKinds.includes("removed") || finding.termChangeKinds.includes("renamed"))) {
      gone.set(finding.term, finding.renamedTo);
    }
  }
  return (after?.labels ?? []).flatMap((label) =>
    label.ontology_terms.filter((term) => gone.has(term)).map((term) => ({ labelKey: label.key, labelName: label.name, term, renamedTo: gone.get(term) ?? null })),
  );
}

/**
 * The label list's stored definition with one link re-targeted (`replacement` an IRI) or
 * dropped (`null`). Works on the definition as stored, so fields the web app does not
 * know (its lifecycle) stay.
 */
export function relinkedDefinition<T extends { readonly labels: readonly Label[] }>(definition: T, link: BrokenLink, replacement: string | null): T {
  return {
    ...definition,
    labels: definition.labels.map((label) => {
      if (label.key !== link.labelKey) {
        return label;
      }
      const terms = label.ontology_terms.flatMap((term) => (term !== link.term ? [term] : replacement === null ? [] : [replacement]));
      return { ...label, ontology_terms: [...new Set(terms)] };
    }),
  };
}
